import { useEffect } from "react";

import { siteConfig } from "@/content/site";

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function upsertJsonLd(id, payload) {
  let element = document.head.querySelector(`script[data-seo-id="${id}"]`);

  if (!element) {
    element = document.createElement("script");
    element.setAttribute("type", "application/ld+json");
    element.setAttribute("data-seo-id", id);
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(payload);
}

function createStructuredData(seo) {
  const canonicalUrl = `${siteConfig.domain}${seo.canonicalPath}`;
  const socialImageUrl = `${siteConfig.domain}${seo.socialImage}`;
  const sameAsLinks = siteConfig.socialLinks
    .map((link) => link.href)
    .filter((href) => href.startsWith("http"));

  const graph = [
    {
      "@type": "Organization",
      "@id": `${siteConfig.domain}#organization`,
      name: siteConfig.brandName,
      url: siteConfig.domain,
      email: siteConfig.email,
      logo: {
        "@type": "ImageObject",
        url: `${siteConfig.domain}/social-card.svg`,
      },
      sameAs: sameAsLinks,
    },
    {
      "@type": "WebSite",
      "@id": `${siteConfig.domain}#website`,
      url: siteConfig.domain,
      name: siteConfig.brandName,
      publisher: {
        "@id": `${siteConfig.domain}#organization`,
      },
    },
    {
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: seo.title,
      description: seo.description,
      isPartOf: {
        "@id": `${siteConfig.domain}#website`,
      },
      about: {
        "@id": `${siteConfig.domain}#organization`,
      },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: socialImageUrl,
      },
    },
  ];

  if (seo.product) {
    const productUrl = `${siteConfig.domain}${seo.product.urlPath || seo.canonicalPath}`;
    const productImageUrl = seo.product.image.startsWith("http")
      ? seo.product.image
      : `${siteConfig.domain}${seo.product.image}`;

    graph.push({
      "@type": "Product",
      "@id": `${productUrl}#product`,
      name: seo.product.name,
      description: seo.product.description,
      image: productImageUrl,
      brand: {
        "@type": "Brand",
        name: seo.product.brand || siteConfig.brandName,
      },
      offers: {
        "@type": "Offer",
        url: productUrl,
        price: seo.product.price,
        priceCurrency: seo.product.priceCurrency,
        availability: seo.product.availability,
      },
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export default function PageSeo({ seo }) {
  useEffect(() => {
    const canonicalUrl = `${siteConfig.domain}${seo.canonicalPath}`;
    const socialImageUrl = `${siteConfig.domain}${seo.socialImage}`;
    const structuredData = createStructuredData(seo);
    const robots = seo.robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "author", siteConfig.brandName);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", siteConfig.brandName);
    upsertMeta("property", "og:locale", "en_US");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", socialImageUrl);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", socialImageUrl);
    upsertLink("canonical", canonicalUrl);
    upsertJsonLd("site-graph", structuredData);
  }, [seo]);

  return null;
}
